import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import API, { dicebear, photoUrl } from '../utils/api';
import useDesktop from '../hooks/useDesktop';
import toast from 'react-hot-toast';
import { X, Archive, ChevronRight } from 'lucide-react';

function timeLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffSec = (now - d) / 1000;
  if (diffSec < 60) return 'now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
  // Same calendar day: show clock time (e.g. "14:35")
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  // Yesterday
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  // This year: "Apr 14"
  if (d.getFullYear() === now.getFullYear())
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  // Older: "Apr 14, 2024"
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ConversationsPanel({ onClose }) {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { conversations, setConversations, unreadCounts, markRoomRead, totalUnread } = useSocket();
  const [hoveredRoom, setHoveredRoom] = useState(null);

  const isMobile = !useDesktop();

  const archiveConv = async (e, conv) => {
    e.stopPropagation();
    try {
      setConversations(prev => prev.filter(c => c.chatRoomId !== conv.chatRoomId));
      await API.put(`/chat/conversations/${conv.chatRoomId}/archive`, { userId: user.uniqueId });
      toast.success(`Conversation with ${conv.otherName.split(' ')[0]} archived`);
    } catch {
      toast.error('Failed to archive');
    }
  };

  const openChat = (conv) => {
    markRoomRead(conv.chatRoomId);
    navigate(`/chat/${conv.chatRoomId}`);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 149,
          background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
          animation: 'fadeIn 0.2s ease forwards',
        }}
      />

      {/* Panel */}
      <div style={isMobile ? {
        position: 'fixed',
        left: 0, right: 0, bottom: 0,
        height: '80vh',
        maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 60px)',
        zIndex: 150,
        background: 'var(--glass-bg)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
        borderTop: '1px solid rgba(255,255,255,0.13)',
        borderRadius: '20px 20px 0 0',
        display: 'flex', flexDirection: 'column',
        animation: 'panelSlideUp 0.3s cubic-bezier(0.22,1,0.36,1) forwards',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 -12px 48px rgba(0,0,0,0.5)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      } : {
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 150,
        width: '360px', maxWidth: '100vw',
        background: 'var(--glass-bg)', backdropFilter: 'blur(32px)', WebkitBackdropFilter: 'blur(32px)',
        borderLeft: '1px solid rgba(255,255,255,0.13)',
        display: 'flex', flexDirection: 'column',
        animation: 'panelSlideIn 0.3s cubic-bezier(0.22,1,0.36,1) forwards',
        boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.08), -12px 0 48px rgba(0,0,0,0.5)',
      }}>

        {isMobile && (
          <div style={{
            width: '36px', height: '4px', borderRadius: '2px',
            background: 'var(--border)', margin: '10px auto 0', flexShrink: 0,
          }} />
        )}

        {/* Header */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', lineHeight: 1 }}>Messages</h2>
            <p style={{ color: 'var(--text3)', fontSize: '0.72rem', marginTop: '4px' }}>
              {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
              {totalUnread > 0 && <span style={{ color: 'var(--accent)', marginLeft: '6px' }}>· {totalUnread} unread</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close messages panel"
            style={{
              background: 'var(--bg3)', border: 'none', borderRadius: '50%',
              width: '36px', height: '36px', cursor: 'pointer',
              color: 'var(--text2)', fontSize: '1rem', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {conversations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text3)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>💬</div>
              <p style={{ fontSize: '0.875rem' }}>No conversations yet.</p>
              <p style={{ fontSize: '0.78rem', marginTop: '6px', lineHeight: 1.6 }}>
                Wish someone on the mosaic to start chatting.
              </p>
            </div>
          ) : (
            conversations.map(conv => {
              const unread  = unreadCounts[conv.chatRoomId] || 0;
              const avatar  = conv.otherPhoto
                ? (photoUrl(conv.otherPhoto) || dicebear(conv.otherName))
                : dicebear(conv.otherName);
              const preview = conv.lastMessage?.message || conv.wishMessage || 'Start the conversation…';
              const time    = timeLabel(conv.lastActivity);

              return (
                <div
                  key={conv.chatRoomId}
                  role="button"
                  tabIndex={0}
                  aria-label={`Chat with ${conv.otherName}${unread > 0 ? `, ${unread} unread` : ''}`}
                  onClick={() => openChat(conv)}
                  onKeyDown={e => e.key === 'Enter' && openChat(conv)}
                  onMouseEnter={() => setHoveredRoom(conv.chatRoomId)}
                  onMouseLeave={() => setHoveredRoom(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '11px',
                    padding: '11px 12px', borderRadius: '12px',
                    cursor: 'pointer', marginBottom: '2px',
                    background: unread > 0 ? 'var(--glow-faint)' : 'transparent',
                    border: `1px solid ${unread > 0 ? 'var(--glow)' : 'transparent'}`,
                    transition: 'background 0.15s, border-color 0.15s',
                    position: 'relative',
                  }}
                >
                  {/* Avatar */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: '46px', height: '46px', borderRadius: '50%',
                      overflow: 'hidden', background: 'var(--bg3)',
                      border: `2px solid ${unread > 0 ? 'var(--accent)' : 'rgba(255,255,255,0.08)'}`,
                      transition: 'border-color 0.2s',
                    }}>
                      <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={e => { e.target.src = dicebear(conv.otherName); }} />
                    </div>
                    {unread > 0 && (
                      <div style={{
                        position: 'absolute', top: '-3px', right: '-3px',
                        background: 'var(--accent)', color: '#fff',
                        borderRadius: '50%', minWidth: '18px', height: '18px',
                        fontSize: '0.6rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid var(--panel-bg)', padding: '0 3px',
                      }}>
                        {unread > 9 ? '9+' : unread}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
                      <span style={{ fontSize: '0.875rem', fontWeight: unread > 0 ? 700 : 500, color: unread > 0 ? 'var(--text)' : 'var(--text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                        {conv.otherName}
                      </span>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text3)', flexShrink: 0, marginLeft: '6px' }}>
                        {time}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '0.78rem',
                      color: unread > 0 ? 'var(--text2)' : 'var(--text3)',
                      fontWeight: unread > 0 ? 500 : 400,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      lineHeight: 1.4,
                    }}>
                      {preview}
                    </div>
                  </div>

                  {/* Archive button (hover) or arrow */}
                  {hoveredRoom === conv.chatRoomId ? (
                    <button
                      onClick={e => archiveConv(e, conv)}
                      aria-label={`Archive conversation with ${conv.otherName}`}
                      title="Archive conversation"
                      style={{
                        background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
                        borderRadius: '8px', color: 'var(--text3)', cursor: 'pointer',
                        fontSize: '0.75rem', padding: '5px 8px', flexShrink: 0,
                        fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: '4px',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; e.currentTarget.style.color = 'var(--color-decline)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                    >
                      <Archive size={13} /> Archive
                    </button>
                  ) : (
                    <ChevronRight size={16} style={{ color: 'var(--text3)', flexShrink: 0 }} />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        @keyframes panelSlideIn {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes panelSlideUp {
          from { transform: translateY(100%); opacity: 0.6; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}
